import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  BottomSheetContainer,
  Colors,
  createUseBottomSheetModal,
  Flex,
  IconChevronRight,
  TextButton,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { GoodsLikeRecommend } from './components/GoodsLikeRecommend';
import { View } from 'react-native';
import { bridge, isAndroid, isNotNil } from 'shared/src/utils';
import { PostGoodsActionContext } from './context/PostGoodsActionContext';
import { useLogViewRecommendedModal } from './hooks/useLogViewRecommendedModal';
import { scheduleOnRN } from 'react-native-worklets';
import { useGoodsSno } from '../../hooks/useGoodsSno';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';
import {
  useAndroidHardwareBackPressHandler,
  useOpenDeeplink,
} from 'shared/src/hooks';
import { AiStudioBanner } from './components/AiStudioBanner';
import { AddCartRecommend } from './components/AddCartRecommend';
import { useIsPostGoodsActionBottomSheetOpen } from '../../atom/isPostGoodsActionBottomSheetOpen.atom';

export type PostGoodsActionBottomSheetParams = {
  image: string;
  mode: 'like' | 'cart';
  title: {
    highlight: string;
    text: string;
  };
  action: {
    text: string;
    deeplink: string;
  };
  folderSno?: number;
};
export const PostGoodsActionBottomSheetContent = ({
  param,
  confirm,
}: {
  param: PostGoodsActionBottomSheetParams;
  confirm: (result: void) => void;
}) => {
    const { height: screenHeight } = useScreenSize();
    const { top, bottom } = useSafeArea();
    const sno = useGoodsSno();
    const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
    const { openDeeplink } = useOpenDeeplink();

    const [, setIsBottomSheetOpen] = useIsPostGoodsActionBottomSheetOpen();
    useEffect(() => {
      setIsBottomSheetOpen(true);
      return () => setIsBottomSheetOpen(false);
    }, [setIsBottomSheetOpen]);

    const viewHeight = screenHeight - top - bottom;
    const defaultInitialHeight = isAndroid
      ? viewHeight * 0.7
      : viewHeight * 0.6;
    const heightAnim = useSharedValue(0);
    const startHeight = useSharedValue(defaultInitialHeight);
    const headerHeight = useSharedValue(0);
    const contentHeight = useSharedValue(0);
    const [isExpanded, setIsExpanded] = useState(false);
    const [carouselCount, setCarouselCount] = useState(0);
    const [contentHeightState, setContentHeightState] = useState(0);

    const maxHeight = useMemo(() => {
      // Android native(STATE_EXPANDED)는 status bar/nav bar 제외 콘텐츠 가용
      // 영역까지 펼치므로 viewHeight(=screenHeight - top - bottom) + bottom
      // = screenHeight - top 으로 계산해 native 정합.
      if (isAndroid) {
        return viewHeight + bottom;
      }
      return screenHeight * 0.9 + bottom;
    }, [viewHeight, screenHeight, bottom]);

    const initialHeight = useMemo(() => {
      if (carouselCount === 1 && contentHeightState > 0) {
        return Math.min(contentHeightState + bottom, maxHeight);
      }
      return defaultInitialHeight;
    }, [
      carouselCount,
      contentHeightState,
      bottom,
      defaultInitialHeight,
      maxHeight,
    ]);

    useEffect(() => {
      heightAnim.value = withTiming(initialHeight, { duration: 300 });
    }, [heightAnim, initialHeight]);

    const style = useAnimatedStyle(() => ({
      height: heightAnim.value + bottom,
    }));

    const gesture = useMemo(
      () =>
        Gesture.Pan()
          .activeOffsetY([-10, 10])
          .onStart(() => {
            startHeight.value = heightAnim.value;
          })
          .onUpdate(event => {
            const currentMaxHeight = Math.max(
              contentHeight.value + bottom,
              initialHeight,
            );
            const clampedMax = Math.min(currentMaxHeight, maxHeight);
            const newHeight = startHeight.value - event.translationY;
            heightAnim.value = Math.max(0, Math.min(clampedMax, newHeight));
          })
          .onEnd(() => {
            const currentMaxHeight = Math.max(
              contentHeight.value + bottom,
              initialHeight,
            );
            const clampedMax = Math.min(currentMaxHeight, maxHeight);

            if (heightAnim.value >= initialHeight) {
              // 초기 높이보다 크면 → max로 스냅
              heightAnim.value = withTiming(clampedMax, { duration: 200 });
              scheduleOnRN(setIsExpanded, true);
            } else {
              // 초기 높이보다 작으면 → 닫기 애니메이션 후 dismiss
              heightAnim.value = withTiming(0, { duration: 200 });
              scheduleOnRN(confirm);
            }
          }),
      [
        bottom,
        confirm,
        contentHeight.value,
        heightAnim,
        initialHeight,
        maxHeight,
        startHeight,
      ],
    );

    useLogViewRecommendedModal(param.mode);

    useAndroidHardwareBackPressHandler(() => {
      confirm();
      return true;
    });

    return (
      <BottomSheetContainer maxHeight={'95%'} hideSafeArea>
        <GestureDetector gesture={gesture}>
          <PostGoodsActionContext.Provider value={{ mode: param.mode }}>
            <Animated.View style={style}>
              <View collapsable={false}>
                <Flex.Center
                  padding={{ top: 8, bottom: 4 }}
                  borderBottomWidth={0}
                  borderColor={'white'}
                >
                  <Flex
                    width={40}
                    height={4}
                    backgroundColorValue={Colors.border_primary}
                    borderRadius={2}
                  />
                </Flex.Center>
              </View>

              <Flex flex={1}>
                <Flex
                  onLayout={({ nativeEvent }) => {
                    // sharedValue로 직접 업데이트 (setState 없이)
                    const newHeight = nativeEvent.layout.height;
                    if (newHeight > headerHeight.value) {
                      headerHeight.value = newHeight;
                    }
                  }}
                >
                  <Flex.Row
                    gap={12}
                    padding={{ vertical: 8, left: 16, right: 4 }}
                    borderColor={'border_tertiary'}
                    borderBottomWidth={1}
                  >
                    <Avatar uri={param.image} size={32} />
                    <Flex flex={1} justifyContent={'center'}>
                      <Typography type={'body1'} color={'content_secondary'}>
                        <Typography
                          type={'subtitle1'}
                          color={'content_secondary'}
                        >
                          {param.title.highlight}
                        </Typography>
                        {param.title.text}
                      </Typography>
                    </Flex>

                    <TextButton
                      color="blue"
                      onPress={() => {
                        if (isNotNil(param.folderSno)) {
                          bridge.logAnalyticsEvent({
                            name: 'CLICK_FOLDER',
                            analytics: {
                              SCREEN: 'GOODS_DETAIL',
                              GOODS_SNO: sno,
                              FOLDER_SNO: param.folderSno.toString(),
                              LANDING_FROM: 'TOAST',
                            },
                          });
                        }
                        if (param.mode === 'cart') {
                          logGoodsAnalytics({
                            name: 'CLICK_CART',
                          });
                        }
                        openDeeplink(param.action.deeplink);
                      }}
                      size="small"
                      icon={<IconChevronRight />}
                    >
                      {param.action.text}
                    </TextButton>
                  </Flex.Row>

                  <AiStudioBanner />
                </Flex>
                <Flex flex={1}>
                  <Flex flex={1}>
                    {param.mode === 'like' && (
                      <GoodsLikeRecommend
                        isExpanded={isExpanded}
                        onContentHeightChange={height => {
                          if (height > contentHeight.value) {
                            contentHeight.value = height + bottom;
                          }
                          setContentHeightState(prev =>
                            height > prev ? height : prev,
                          );
                        }}
                        onCarouselCountChange={setCarouselCount}
                      />
                    )}
                    {param.mode === 'cart' && (
                      <AddCartRecommend
                        isExpanded={isExpanded}
                        onContentHeightChange={height => {
                          if (height > contentHeight.value) {
                            contentHeight.value = height + bottom;
                          }
                          setContentHeightState(prev =>
                            height > prev ? height : prev,
                          );
                        }}
                        onCarouselCountChange={setCarouselCount}
                      />
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Animated.View>
          </PostGoodsActionContext.Provider>
        </GestureDetector>
      </BottomSheetContainer>
    );
};

export const usePostGoodsActionBottomSheet = createUseBottomSheetModal<
  void,
  PostGoodsActionBottomSheetParams
>(
  ({ param, confirm }) => (
    <PostGoodsActionBottomSheetContent param={param} confirm={confirm} />
  ),
  {
    cancelOnBackButtonPress: false,
  },
);
